import Parse from "parse"
import { actionWithLoader, onEnter, push } from "../Utils/utils"
import { openAppMessageSnackBar } from "../Utils/app"
import { loadPackagingData } from "../Packaging/packaging"
import { setValues } from "../../parseUtils"
import { getProductTypes } from "../../parseManager/products/productTypeManager"
import { KFC_BRANDS } from "../../utils"
import { getOrganizationsEnterprisesSelector, getCatalogProductTypesSelector } from "../../reducers/Organizations/organizations"
import { hasOngoingDeliveriesForEnterprise } from "../../parseManager/organizations/deliveriesManager"

export const ONGOING_DELIVERIES_ERROR_MESSAGE = "Il n'est pas possible d'archiver cette entreprise car au moins une livraison est en cours."

const ENTERPRISE_PROPERTIES = new Set([
  "name",
  "code",
  "logo",
  "color",
  "packagingNotes",
  "cardDesignNotes",
  "requiresValidation",
  "nationalSendField",
  "sendCapitalField",
  "smallRetailField",
  "lunchbagField",
])

const Enterprise = Parse.Object.extend("Enterprises")

export const loadEnterprises = () =>
  actionWithLoader(async (dispatch) => {
    const enterprises = await new Parse.Query(Enterprise)
      .ascending("name")
      .find()

    dispatch({ type: "ENTERPRISES_LOADED", enterprises: enterprises.map(e => e.toJSON()) })
  })

export const createEnterprise = (values) =>
  actionWithLoader(async (dispatch) => {
    const existing = await new Parse.Query(Enterprise)
      .equalTo("code", values.code)
      .first()

    if (existing) {
      return { codeAlreadyExists: true }
    }

    const enterprise = new Enterprise()

    setValues(enterprise, values, ENTERPRISE_PROPERTIES)

    const saved = await enterprise.save()

    dispatch({ type: "ENTERPRISE_CREATED", enterprise: saved.toJSON() })
  })

export const loadEnterprise = (params) =>
  actionWithLoader(async (dispatch) => {
    const enterprise = await new Parse.Query(Enterprise)
      .equalTo("objectId", params.id)
      .first()

    dispatch({ type: "ENTERPRISE_LOADED", enterprise: enterprise ? enterprise.toJSON() : null })
  })

export const loadAvailableDCs = () =>
  actionWithLoader(async (dispatch) => {
    const dcs = await new Parse.Query("DistributionCenters")
      .equalTo("isActive", true)
      .limit(1000) // just in case, should not be that many
      .ascending("name")
      .find()

    dispatch({ type: "AVAILABLE_DCS_LOADED", availableDCs: dcs.map(d => d.toJSON()) })
  })

export const attachDistributionCenters = (enterpriseId, newLinks) =>
  actionWithLoader(async (dispatch) => {
    const enterprise = await new Parse.Query(Enterprise)
      .equalTo("objectId", enterpriseId)
      .first()

    const existing = enterprise.get("distributionCenters") || []
    const existingIds = new Set(existing.map(l => l.dc?.objectId))
    const toAdd = newLinks.filter(l => !existingIds.has(l.dc?.objectId))

    enterprise.set("distributionCenters", [...existing, ...toAdd])
    const saved = await enterprise.save()

    dispatch({ type: "ENTERPRISE_DCS_UPDATED", enterprise: saved.toJSON() })
  })

export const updateDistributionCenterLink = (enterpriseId, updatedLink) =>
  actionWithLoader(async (dispatch) => {
    const enterprise = await new Parse.Query(Enterprise)
      .equalTo("objectId", enterpriseId)
      .first()

    const existing = enterprise.get("distributionCenters") || []
    enterprise.set(
      "distributionCenters",
      existing.map(l => l.dc?.id === updatedLink.dc?.objectId ? updatedLink : l)
    )
    const saved = await enterprise.save()

    dispatch({ type: "ENTERPRISE_DCS_UPDATED", enterprise: saved.toJSON() })
  })

export const detachDistributionCenter = (enterpriseId, dcId) => {
  return actionWithLoader(async (dispatch) => {
    const enterprise = await new Parse.Query(Enterprise)
      .equalTo("objectId", enterpriseId)
      .first()

    const existing = enterprise.get("distributionCenters") || []
    enterprise.set("distributionCenters", existing.filter(l => l.dc?.id !== dcId))
    const saved = await enterprise.save()

    dispatch({ type: "ENTERPRISE_DCS_UPDATED", enterprise: saved.toJSON() })
  })
}

export const addContact = (enterpriseId, contact) =>
  actionWithLoader(async (dispatch) => {
    const enterprise = await new Parse.Query(Enterprise)
      .equalTo("objectId", enterpriseId)
      .first()

    const existing = enterprise.get("contacts") || []
    enterprise.set("contacts", [...existing, contact])
    const saved = await enterprise.save()

    dispatch({ type: "ENTERPRISE_CONTACTS_UPDATED", enterprise: saved.toJSON() })
  })

export const updateContact = (enterpriseId, updatedContact) =>
  actionWithLoader(async (dispatch) => {
    const enterprise = await new Parse.Query(Enterprise)
      .equalTo("objectId", enterpriseId)
      .first()

    const existing = enterprise.get("contacts") || []
    enterprise.set("contacts", existing.map(c => c.id === updatedContact.id ? updatedContact : c))
    const saved = await enterprise.save()

    dispatch({ type: "ENTERPRISE_CONTACTS_UPDATED", enterprise: saved.toJSON() })
  })

export const deleteContact = (enterpriseId, contactId) =>
  actionWithLoader(async (dispatch) => {
    const enterprise = await new Parse.Query(Enterprise)
      .equalTo("objectId", enterpriseId)
      .first()

    const existing = enterprise.get("contacts") || []
    enterprise.set("contacts", existing.filter(c => c.id !== contactId))
    const saved = await enterprise.save()

    dispatch({ type: "ENTERPRISE_CONTACTS_UPDATED", enterprise: saved.toJSON() })
  })

export const updateEnterpriseGeneralInfo = (enterpriseId, values) =>
  actionWithLoader(async (dispatch) => {
    const enterprise = await new Parse.Query(Enterprise)
      .equalTo("objectId", enterpriseId)
      .first()

    setValues(enterprise, values, ENTERPRISE_PROPERTIES)
    const saved = await enterprise.save()

    dispatch({ type: "ENTERPRISE_UPDATED", enterprise: saved.toJSON() })
  })

export const updateEnterpriseLabelTemplates = (enterpriseId, labelTemplates) =>
  actionWithLoader(async (dispatch) => {
    const enterprise = await new Parse.Query(Enterprise)
      .equalTo("objectId", enterpriseId)
      .first()

    enterprise.set("labelTemplates", labelTemplates)
    const saved = await enterprise.save()

    dispatch({ type: "ENTERPRISE_UPDATED", enterprise: saved.toJSON() })
  })

export const loadCatalogSourceProductTypes = () =>
  actionWithLoader(async (dispatch) => {
    const productTypes = await getProductTypes()

    dispatch({ type: "CATALOG_PRODUCT_TYPES_LOADED", catalogProductTypes: productTypes })
  })

export const getAvailableProductTypes = (sourceType, source) =>
  actionWithLoader(async () => {
    return await Parse.Cloud.run("getAvailableProductTypesForSource", { sourceType, source })
  })

export const previewCatalogGeneration = (enterpriseId, sources) =>
  actionWithLoader(async () => {
    return await Parse.Cloud.run("previewEnterpriseCatalogGeneration", { enterpriseId, sources })
  })

const formatProductTypesList = (productTypes, catalogProductTypes) => {
  const labels = productTypes.map((kfcName) => {
    const productType = catalogProductTypes.find((pt) => pt.kfcName === kfcName)
    return productType ? productType.label : kfcName
  })

  if (labels.length <= 1) return labels[0] || ""

  return `${labels.slice(0, -1).join(", ")} et ${labels[labels.length - 1]}`
}

const getCatalogSourceLabel = (sourceType, source, enterprises) => {
  if (sourceType === "BRAND") {
    const brand = KFC_BRANDS.find((b) => b.name === source)
    return brand ? brand.label : source
  }

  const sourceEnterprise = enterprises.find((e) => e.objectId === source)
  return sourceEnterprise ? sourceEnterprise.name : source
}

export const generateEnterpriseCatalog = (enterpriseId, sources) =>
  actionWithLoader(async (dispatch, getState) => {
    try {
      const enterprise = await Parse.Cloud.run("generateEnterpriseCatalog", { enterpriseId, sources })

      dispatch({ type: "ENTERPRISE_UPDATED", enterprise })

      const state = getState()
      const enterprises = getOrganizationsEnterprisesSelector(state)
      const catalogProductTypes = getCatalogProductTypesSelector(state)

      sources.forEach(({ sourceType, source, productTypes }) => {
        const sourceLabel = getCatalogSourceLabel(sourceType, source, enterprises)
        const productTypesLabel = formatProductTypesList(productTypes, catalogProductTypes)
        const article = productTypes.length > 1 ? "des" : "de"

        dispatch(openAppMessageSnackBar(
          `Le catalogue ${article} ${productTypesLabel} de ${sourceLabel} a bien été ajouté à votre entreprise.`,
          "success",
          5000,
          {},
          "LIST"
        ))
      })

      return true
    } catch (e) {
      dispatch(openAppMessageSnackBar("Une erreur est survenue, veuillez recommencer.", "error", 5000))
      return false
    }
  })

export const removeCatalogSource = (enterpriseId, sourceType, source) =>
  actionWithLoader(async (dispatch) => {
    const enterprise = await new Parse.Query(Enterprise)
      .equalTo("objectId", enterpriseId)
      .first()

    const existing = enterprise.get("catalogSources") || []
    enterprise.set("catalogSources", existing.filter(s => !(s.sourceType === sourceType && s.source === source)))
    const saved = await enterprise.save()

    dispatch({ type: "ENTERPRISE_UPDATED", enterprise: saved.toJSON() })
  })

export const updateEnterpriseLogo = (enterpriseId, { logo, color }) =>
  actionWithLoader(async (dispatch) => {
    const enterprise = await new Parse.Query(Enterprise)
      .equalTo("objectId", enterpriseId)
      .first()

    const previousLogoPublicId = enterprise.get("logo")?.publicId
    const nextLogoPublicId = logo?.publicId
    const previousIconPublicId = enterprise.get("logo")?.publicId
    const nextIconPublicId = logo?.publicId

    setValues(enterprise, { logo, color }, ENTERPRISE_PROPERTIES)
    const saved = await enterprise.save()

    if (previousLogoPublicId && previousLogoPublicId !== nextLogoPublicId) {
      try {
        await Parse.Cloud.run("deleteCloudinaryImage", { publicId: previousLogoPublicId })
      } catch (e) {
        // best-effort cleanup, the enterprise update itself already succeeded
      }
    }

    if (previousIconPublicId && previousIconPublicId !== nextIconPublicId) {
      try {
        await Parse.Cloud.run("deleteCloudinaryImage", { publicId: previousIconPublicId })
      } catch (e) {
        // best-effort cleanup, the enterprise update itself already succeeded
      }
    }

    dispatch({ type: "ENTERPRISE_UPDATED", enterprise: saved.toJSON() })
  })

export const archiveEnterprise = (enterpriseId) =>
  actionWithLoader(async (dispatch) => {
    const enterprise = await new Parse.Query(Enterprise)
      .equalTo("objectId", enterpriseId)
      .first()

    const hasOngoingDeliveries = await hasOngoingDeliveriesForEnterprise(enterpriseId)
    if (hasOngoingDeliveries) {
      throw new Error(ONGOING_DELIVERIES_ERROR_MESSAGE)
    }

    enterprise.set("status", "ARCHIVED")
    const saved = await enterprise.save()

    dispatch({ type: "ENTERPRISE_UPDATED", enterprise: saved.toJSON() })
    return true
  })

export const reactivateEnterprise = (enterpriseId) =>
  actionWithLoader(async (dispatch) => {
    const enterprise = await new Parse.Query(Enterprise)
      .equalTo("objectId", enterpriseId)
      .first()

    enterprise.set("status", "ACTIVE")
    const saved = await enterprise.save()

    dispatch({ type: "ENTERPRISE_UPDATED", enterprise: saved.toJSON() })
    return true
  })

export const onEnterprisesEnter = (store) => {
  return onEnter({
      store,
      actionThunk: loadEnterprises
  })
}

export const saveAndSyncEnterprisePackagingSettings = (enterpriseId, settings) =>
  actionWithLoader(async (dispatch) => {
    try {
      const result = await Parse.Cloud.run("saveAndSyncEnterprisePackagingSettings", { enterpriseId, settings })
      dispatch({ type: "ENTERPRISE_UPDATED", enterprise: result.enterprise })
      dispatch(openAppMessageSnackBar(
        `Conditionnement synchronisé — ${result.updatedCount} recette${result.updatedCount !== 1 ? "s" : ""} mise${result.updatedCount !== 1 ? "s" : ""} à jour.`,
        "success",
        5000
      ))
      return result
    } catch (e) {
      dispatch(openAppMessageSnackBar("Une erreur est survenue, veuillez réessayer.", "error", 5000))
      return false
    }
  })

export const onEnterEnterprise = (store) => {
  return onEnter({
    store,
    actionThunk: (params) => async (dispatch) => {
      await dispatch(loadEnterprise(params))
      await dispatch(loadAvailableDCs())
      await dispatch(loadEnterprises())
      await dispatch(loadCatalogSourceProductTypes())
      await dispatch(loadPackagingData())
    }
  })
}

export const showEnterprises = () => push("/organizations/enterprises")

export const showEnterprise = (id) => push(`/organizations/enterprises/${id}`)
